// Exit-animation helper for overlay surfaces (modal, bottom sheet, toasts).
//
// Plays the given exit animation class, then removes the element. Falls back
// to synchronous removal when the environment can't animate (jsdom in tests
// has no Element.getAnimations) or the user prefers reduced motion — callers
// can therefore keep treating removal as logically immediate; only the
// visual teardown is deferred.

export function reducedMotion() {
  try {
    return !window.matchMedia || window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return true;
  }
}

/**
 * @param {HTMLElement} el
 * @param {string} animClass  e.g. 'anim-fade-out', 'anim-sheet-down'
 */
export function exitAndRemove(el, animClass) {
  if (!el || !el.isConnected) return;
  if (reducedMotion() || typeof el.getAnimations !== 'function') {
    el.remove();
    return;
  }
  // The element is logically gone — don't let it swallow taps while fading.
  el.classList.add('pointer-events-none', animClass);
  let removed = false;
  const done = () => {
    if (removed) return;
    removed = true;
    el.remove();
  };
  el.addEventListener('animationend', done, { once: true });
  // Fallback in case animationend never fires (display:none ancestors etc.).
  setTimeout(done, 400);
}

/**
 * FLIP reorder animation for a list of cards.
 *
 * Records card positions before a re-render mutation, then animates each card
 * that moved from its old position to its new one using a spring transition.
 * Cards added or removed by the mutation are not animated here — use
 * anim-fade-up / exitAndRemove for those.
 *
 * @param {() => NodeList|Array<HTMLElement>} getCards
 *   Returns the live card elements (called twice: before and after mutate).
 * @param {() => void} mutate
 *   Re-render callback (e.g. applyFilter). Called synchronously between reads.
 * @param {{ idFn?: (el: HTMLElement) => string, skipAboveCount?: number }} opts
 *   idFn maps each element to its stable item id; defaults to el.dataset.id.
 *   skipAboveCount skips animation when card count exceeds this value (default 30).
 */
export function flipReorder(getCards, mutate, { idFn = (el) => el.dataset.id, skipAboveCount = 30 } = {}) {
  // Early-exit guards: reduced motion, no animation support (jsdom), or list too long.
  if (reducedMotion() || typeof document.createElement('div').getAnimations !== 'function') {
    mutate();
    return;
  }

  // First — read all positions before mutation (one pass, no writes).
  const beforeCards = Array.from(getCards());
  if (beforeCards.length > skipAboveCount) {
    mutate();
    return;
  }
  /** @type {Map<string, number>} */
  const firstTops = new Map();
  for (const el of beforeCards) {
    firstTops.set(idFn(el), el.getBoundingClientRect().top);
  }

  // Mutate — re-render the list.
  mutate();

  // Last — read all positions after mutation (one pass, no writes).
  const afterCards = Array.from(getCards());
  /** @type {Map<string, HTMLElement>} */
  const afterEls = new Map();
  /** @type {Map<string, number>} */
  const lastTops = new Map();
  for (const el of afterCards) {
    const id = idFn(el);
    afterEls.set(id, el);
    lastTops.set(id, el.getBoundingClientRect().top);
  }

  // Invert — apply translateY(delta) instantly to every element that moved
  // and is present in both snapshots (one write pass, no interleaved reads).
  /** @type {Array<HTMLElement>} */
  const toPlay = [];
  for (const [id, firstTop] of firstTops) {
    if (!lastTops.has(id)) continue; // removed — handled by exitAndRemove
    const delta = firstTop - lastTops.get(id);
    if (delta === 0) continue; // did not move
    const el = afterEls.get(id);
    el.style.transition = 'none';
    el.style.transform = `translateY(${delta}px)`;
    toPlay.push(el);
  }

  if (toPlay.length === 0) return;

  // Play — on the next two animation frames clear the inversion and let the
  // spring transition carry each card to its natural (transform: none) position.
  // Double-rAF ensures the browser has committed the inverted style before we
  // start the transition so the interpolation is visible.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      for (const el of toPlay) {
        el.style.transition = 'transform 0.25s cubic-bezier(0.2,0.8,0.3,1)';
        el.style.transform = '';
      }
      // Clean up inline styles once the transition settles.
      for (const el of toPlay) {
        el.addEventListener('transitionend', () => {
          el.style.transition = '';
          el.style.transform = '';
        }, { once: true });
      }
    });
  });
}
